import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport } from '@feathr/blackbox';
import { ConfirmModal, ContextMenu } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import {
  getArchiveConfirmCopy,
  getStopConfirmCopy,
  useExportRowActions,
} from './ExportRowActions.hooks';

interface IExportRowActionsProps {
  record: SegmentExport;
}

function ExportRowActions({ record }: Readonly<IExportRowActionsProps>): JSX.Element {
  const { t } = useTranslation();
  const { canStop, canArchive, canDownload, isExpired, isStopping } = record;
  const { stopModal, archiveModal, onConfirmStop, onConfirmArchive, onDownload } =
    useExportRowActions(record);

  const stopCopy = getStopConfirmCopy(record, t);
  const archiveCopy = getArchiveConfirmCopy(record, t);

  return (
    <>
      <ContextMenu buttonType={'icon'} name={'export-row-actions'} position={'left-start'}>
        {(canDownload || isExpired) && (
          <ContextMenu.Item
            disabled={isExpired}
            onClick={onDownload}
            prefix={getIconForAction('download')}
            tooltip={
              isExpired ? t('The download link has expired. Reinitiate the export.') : undefined
            }
          >
            {t('Download')}
          </ContextMenu.Item>
        )}
        {(canStop || isStopping) && (
          <ContextMenu.Item
            disabled={isStopping}
            onClick={stopModal.open}
            prefix={getIconForAction('stop')}
            theme={'danger'}
            tooltip={isStopping ? t('This export is already stopping.') : undefined}
          >
            {t('Stop')}
          </ContextMenu.Item>
        )}
        {canArchive && (
          <ContextMenu.Item
            onClick={archiveModal.open}
            prefix={getIconForAction('archive')}
            theme={'danger'}
          >
            {t('Archive')}
          </ContextMenu.Item>
        )}
      </ContextMenu>
      <ConfirmModal
        alertDescription={stopCopy.alertDescription}
        alertTitle={stopCopy.alertTitle}
        alertType={'warning'}
        cancelButtonText={t('Back')}
        confirmButtonText={stopCopy.confirmButtonText}
        confirmButtonType={'danger'}
        onClose={stopModal.close}
        onConfirm={onConfirmStop}
        opened={stopModal.isOpen}
        t={t}
        title={stopCopy.title}
      />
      <ConfirmModal
        alertDescription={archiveCopy.alertDescription}
        alertTitle={archiveCopy.alertTitle}
        alertType={'warning'}
        cancelButtonText={t('Back')}
        confirmButtonText={archiveCopy.confirmButtonText}
        confirmButtonType={'danger'}
        onClose={archiveModal.close}
        onConfirm={onConfirmArchive}
        opened={archiveModal.isOpen}
        t={t}
        title={archiveCopy.title}
      />
    </>
  );
}

export default observer(ExportRowActions);
